import React from "react"
import { View, StyleSheet, Platform } from "react-native"
import InfoIcon from "@/assets/icons/tooltip-i-notfilled.svg"
import { type AnalysisType } from "@/generated/graphql"
import Tooltip from "rn-tooltip"
import ToolTips from "@/constants/ToolTips"
import { BFColors } from "@/constants/Colors"
import BFText, { H3, Small } from "@/components/standard/BFText"
import { useAnalyzeContext } from "@/components/analysis/AnalyzeContext"
import { $subjectPropertyWalkthroughDismissed } from "@/stores/walkThroughs"
import { useStore } from "@nanostores/react"
import { CopilotStep } from "react-native-copilot"
import { WalkthroughableTouchableOpacity } from "@/components/copilot/WalkthroughableComponents"
import { SpecialtyButton } from "@/components/buttons/CommonButtons"
import SpecialtyFind from "@/assets/icons/specialty-find.svg"
import SpecialtySort from "@/assets/icons/specialty-sort.svg"
import SpecialtyFilter from "@/assets/icons/specialty-filter.svg"
import { Horizontal } from "@/components/standard/BFLayout"
import { currentCriterionStore } from "@/stores/compSortingDataStore" // Import current sorting state
import { getSortButtonTitle } from "@/utils/AnalysisUtils"
import { useIsDesktop } from "@/hooks/useIsDesktop"

interface AnalysisCompsSelectedProps {
  analysis: AnalysisType
  displaysIntroductionText: boolean
}

const AnalysisCompsSelected: React.FC<AnalysisCompsSelectedProps> = ({
  analysis,
  displaysIntroductionText,
}) => {
  const {
    onCustomCompsPress,
    onFindCompsPress,
    onSortCompsPress,
    isFirstManualAnalysisDisplay,
  } = useAnalyzeContext()
  const subjectPropertyWalkthroughDismissed = useStore(
    $subjectPropertyWalkthroughDismissed
  )
  const currentCriterion = useStore(currentCriterionStore)

  // Determine the title for the Sort button
  const sortButtonTitle =
    currentCriterion &&
    currentCriterion !== "suggested" &&
    currentCriterion !== "updatedAt"
      ? getSortButtonTitle(currentCriterion) // Use label from sortOptions if found
      : "Sort" // Default title when criterion is "suggested" or "updatedAt"

  const isDesktop = useIsDesktop()

  return (
    <View className="bg-off-white pb-4 overflow-visible">
      <View className="flex-row items-center p-4 pb-2 pt-[20px] w-full mx-3">
        <H3>Customize Your Comps</H3>

        <Tooltip
          popover={
            <BFText className="font-spacegrotesk text-white text-[12px] p-[2px]">
              {ToolTips.compsSelected}
            </BFText>
          }
          actionType="press"
          backgroundColor={BFColors.calmBlue}
          withOverlay={false}
          height={"auto"}
          width={250}
          containerStyle={{
            borderRadius: 2,
            marginBottom: Platform.OS === "android" ? -21 : 0, // Fixes odd arrow placement that occurred only on Android
          }}
          toggleWrapperProps={{
            hitSlop: 42,
          }}
        >
          <View className="pl-1">
            <InfoIcon width={15} height={15} />
          </View>
        </Tooltip>
      </View>
      {displaysIntroductionText && (
        <Small className="px-4 pb-2 mx-2">
          Customize comps your way. We’ve got you started with 3.
        </Small>
      )}

      <Horizontal className="gap-2 px-4 mx-2">
        <CopilotStep
          text={ToolTips.copilotCustomComps}
          order={5} // Only step to show when onboarding through non-first-manual-analysis; last step otherwise
          name="adjust filters"
          active={
            !subjectPropertyWalkthroughDismissed &&
            !isFirstManualAnalysisDisplay
          }
        >
          <WalkthroughableTouchableOpacity
            onPress={() => {
              onCustomCompsPress()
            }}
            style={styles.button}
          >
            <SpecialtyButton
              className={isDesktop ? "flex-1" : "flex-1 shrink-0"}
              title="Filter"
              testID="filter-comps"
              icon={SpecialtyFilter}
              onPress={() => {
                onCustomCompsPress()
              }}
            />
          </WalkthroughableTouchableOpacity>
        </CopilotStep>

        <SpecialtyButton
          className={isDesktop ? "flex-1" : "flex-1 shrink-0 max-w-[100px]"}
          title={sortButtonTitle}
          testID="sort-comps"
          icon={SpecialtySort}
          onPress={() => {
            onSortCompsPress()
          }}
        />
        <SpecialtyButton
          className={isDesktop ? "flex-1" : "flex-1 shrink-0"}
          title="Find A Comp"
          testID="find-a-comp"
          icon={SpecialtyFind}
          onPress={() => {
            onFindCompsPress(analysis.property.placeId || "")
          }}
        />
      </Horizontal>
    </View>
  )
}

export default AnalysisCompsSelected

const styles = StyleSheet.create({
  buttonContainer: {
    paddingHorizontal: 2,
    marginTop: 0,
  },
  button: {
    paddingTop: 0,
    paddingHorizontal: 2,
    borderRadius: 4,
  },
  buttonText: {
    fontSize: 12,
    color: BFColors.calmBlue,
    textDecorationLine: "underline",
    textAlign: "center",
    marginHorizontal: 5,
  },
})
